import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { getAddressSuggestions } from '@uplift/api';
import { AddressSuggestionsInput, AddressSuggestionType } from '@uplift/types';

export type FetchAddressHookTypes = {
  text: string;
  constraint: boolean;
  debounceMs?: number;
};

// constraint value must be memoized and its value should
// only be recomputed when the text changes
// ex: const contraint: boolean = useMemo(() => text.length >= 6, [ text ]);
export const useFetchAddressSuggestions = ({ text, constraint, debounceMs = 400 }: FetchAddressHookTypes) => {
  const [ suggestions, setSuggestions ] = useState<AddressSuggestionType[]>([]);
  const [ loading, setLoading ] = useState(false);

  const fetchAddressSuggestions = useCallback((text: string) => {
    const data: AddressSuggestionsInput = { text };

    return getAddressSuggestions(data);
  }, []);

  const debouncedFetch = useMemo(() => debounce((text: string) => {
    fetchAddressSuggestions(text).then((addressSuggestions) => {
      setSuggestions(addressSuggestions);
      setLoading(false);
    }).catch(() => {
      setSuggestions([] as AddressSuggestionType[]);
      setLoading(false);
    });
  }, debounceMs), [ debounceMs, fetchAddressSuggestions ]);

  useEffect(() => {
    if (!constraint) {
      setSuggestions([]);
      setLoading(false);
      return;
    };
    setLoading(true);
    debouncedFetch(text);
  }, [ text, constraint, debouncedFetch ]);

  return { suggestions, loading };
};
